/**
 * Accent preference — the persistence + colour-math primitive behind
 * `AccentProvider`/`AccentPicker` (IB-258). Mirrors `themePreference.ts`'s
 * shape exactly (same `localStorage`-only stance, same disclosed
 * server-sync TODO — see that module's header for the rationale) so the two
 * preferences read as one consistent pattern rather than two.
 *
 * TODO(IB-258): server-sync — once a real per-user preference endpoint
 * exists, extend `read`/`write` to hydrate from and PUT to it, keeping this
 * exact read/write shape so `AccentProvider` and the inline bootstrap script
 * in `index.html` don't need to change.
 *
 * The 30 preset hex values below (design doc §4) and the custom-picker value
 * a user supplies are the **one other** sanctioned place for literal hex in
 * the frontend, alongside `src/brand/BrandMark.tsx` — this module is a data
 * source analogous to `tokens.css` itself (it feeds `--iw-accent` at
 * runtime), not a component with a hard-coded colour. No component may
 * hard-code a hex value; they all read `--iw-accent*` custom properties.
 */

/** `null` = "Default" (sponsor graphite, untouched). Otherwise a `#rrggbb` hex string. */
export type AccentPreference = string | null

export const ACCENT_STORAGE_KEY = 'iw.accent-preference'

/**
 * Initial value shown in the native `<input type="color">` before the user
 * has ever picked a custom colour. A native colour input cannot take a CSS
 * var, so it needs a concrete hex — kept here in the sanctioned data module
 * (not hard-coded in the component) per CLAUDE.md §9. Graphite, so the
 * dialog opens on the product's own default accent.
 */
export const CUSTOM_INPUT_FALLBACK = '#5b6b8c'

/**
 * The 30 curated soft-pastel presets (design doc §4 — authoritative, exact
 * values). Hue-ordered, deliberately excluding the retired violet/indigo
 * band (the three specific hexes CLAUDE.md §4 names as retired) per the
 * sponsor directive. `#4FC79E` (Emerald, #15) intentionally matches the
 * brand mark family for a "match my logo" pick.
 */
export const ACCENT_PRESETS: ReadonlyArray<{ name: string; hex: string }> = [
  { name: 'Rose', hex: '#D48A9B' },
  { name: 'Blush', hex: '#E0A3A6' },
  { name: 'Coral', hex: '#E39B82' },
  { name: 'Terracotta', hex: '#D68A6E' },
  { name: 'Peach', hex: '#E6B58C' },
  { name: 'Apricot', hex: '#E1A96A' },
  { name: 'Amber', hex: '#D6A24E' },
  { name: 'Sand', hex: '#CBB185' },
  { name: 'Butter', hex: '#D9C86E' },
  { name: 'Citron', hex: '#C2C46B' },
  { name: 'Olive', hex: '#A9B36A' },
  { name: 'Lime', hex: '#9FC079' },
  { name: 'Sage', hex: '#8FB98A' },
  { name: 'Fern', hex: '#77B08C' },
  { name: 'Emerald', hex: '#4FC79E' },
  { name: 'Mint', hex: '#82C7AB' },
  { name: 'Seafoam', hex: '#7FC9BC' },
  { name: 'Teal', hex: '#6FB6BC' },
  { name: 'Aqua', hex: '#79B8CB' },
  { name: 'Cyan', hex: '#72AEC9' },
  { name: 'Sky', hex: '#7FAAD4' },
  { name: 'Azure', hex: '#6E9BD1' },
  { name: 'Cornflower', hex: '#8AA6DE' },
  { name: 'Denim', hex: '#7C93C0' },
  { name: 'Slate blue', hex: '#8896C4' },
  { name: 'Clay', hex: '#C79A8E' },
  { name: 'Taupe', hex: '#B3A394' },
  { name: 'Stone', hex: '#A6ADB8' },
  { name: 'Steel', hex: '#8FA0B3' },
  { name: 'Graphite Light', hex: '#7A88A8' },
]

const HEX_RE = /^#[0-9a-fA-F]{6}$/

/** Guards both the custom `<input type=color>` value and anything read back out of storage. */
export function isValidHex(value: unknown): value is string {
  return typeof value === 'string' && HEX_RE.test(value)
}

/**
 * Reads the persisted accent preference. Never throws — a storage failure
 * (private browsing, disabled storage, quota) or an unset/corrupt value
 * falls back to `null` ("Default" — sponsor graphite untouched).
 */
export function readAccentPreference(): AccentPreference {
  try {
    const raw = window.localStorage.getItem(ACCENT_STORAGE_KEY)
    return isValidHex(raw) ? raw : null
  } catch {
    return null
  }
}

/**
 * Persists the preference. `null` clears storage (reset to Default).
 * Silently no-ops on storage failure (state still applies for the session).
 */
export function writeAccentPreference(preference: AccentPreference): void {
  try {
    if (preference === null) {
      window.localStorage.removeItem(ACCENT_STORAGE_KEY)
    } else {
      window.localStorage.setItem(ACCENT_STORAGE_KEY, preference)
    }
  } catch {
    // Storage unavailable — the in-memory/React state still reflects the choice this session.
  }
}

/** One sRGB channel (0-255) linearized per the standard relative-luminance formula. */
function linearizeChannel(channel: number): number {
  const c = channel / 255
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

/**
 * Standard sRGB relative luminance (WCAG formula): linearize each channel,
 * then weight 0.2126 / 0.7152 / 0.0722 (R/G/B). Returns a value in `[0, 1]`.
 * Falls back to `0` for a malformed hex (a defensive default that yields
 * white contrast text — never used in practice, callers pass `isValidHex`-
 * guarded values).
 */
export function relativeLuminance(hex: string): number {
  if (!isValidHex(hex)) return 0
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return 0.2126 * linearizeChannel(r) + 0.7152 * linearizeChannel(g) + 0.0722 * linearizeChannel(b)
}

/** `--iw-neutral-900`'s literal value — the dark-ink candidate for on-accent text/icons. */
const DARK_INK = '#1f2533'
/** The white candidate for on-accent text/icons. */
const WHITE_INK = '#ffffff'

/**
 * WCAG contrast ratio between two relative luminances (both already in
 * `[0, 1]`) — `(L_lighter + 0.05) / (L_darker + 0.05)`, always `>= 1`.
 *
 * Exported (IB-291) so `chartPalette.ts`'s WCAG non-text-contrast clamp
 * reuses this exact formula rather than re-implementing it — one WCAG
 * contrast function in the codebase, never two (design doc §3 note).
 */
export function contrastRatio(luminanceA: number, luminanceB: number): number {
  const lighter = Math.max(luminanceA, luminanceB) + 0.05
  const darker = Math.min(luminanceA, luminanceB) + 0.05
  return lighter / darker
}

/**
 * The on-accent text/icon colour (design doc §3, corrected): picks whichever
 * of the two real candidate inks — dark (`#1f2533`, `--iw-neutral-900`) or
 * white — has the HIGHER WCAG contrast ratio against the accent fill. This
 * is not a luminance threshold; a fixed "luminance > X" cutoff picks the
 * WRONG ink across a wide band, because the two candidate inks sit at very
 * different luminances themselves (dark ink ≈0.017, white = 1.0), so the
 * true legibility crossover is far from the midpoint of `[0, 1]` (it's
 * around luminance ≈0.21, not 0.55). Comparing actual contrast ratios is
 * the only way to get this right for every accent, preset or custom.
 * `color-mix()` cannot do this branch — it needs to be computed here and
 * applied inline as `--iw-accent-contrast` by `AccentProvider`.
 */
export function accentContrast(hex: string): string {
  const accentLuminance = relativeLuminance(hex)
  const darkContrast = contrastRatio(accentLuminance, relativeLuminance(DARK_INK))
  const whiteContrast = contrastRatio(accentLuminance, relativeLuminance(WHITE_INK))
  return darkContrast >= whiteContrast ? DARK_INK : WHITE_INK
}
